import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { Modal } from '@carbon/react';
import routes from '~/routes';
import { SuccessToast } from 'components/success-toast';
import ErrorToast from 'components/error-toast';
import {
  contractsAtom,
  curvesAtom,
  useRefreshContracts,
  useRefreshCurves,
} from 'pages/pricing/shared/atoms';
import { pricingApi } from 'pages/pricing/shared/api';
import type { Contract, CurveOption } from 'pages/pricing/shared/types';
import { ContractLibrary } from './contract-library';
import { ContractEditor } from './contract-editor';

type Toast = { type: 'success' | 'error'; message: string; subtitle?: string };

export function ContractBuilderContent() {
  const allContracts = useAtomValue(contractsAtom);
  const curves = useAtomValue(curvesAtom);
  const refreshContracts = useRefreshContracts();
  const refreshCurves = useRefreshCurves();

  // Selection lives in the URL: /pricing/contract-builder (list) vs .../:id (detail).
  // This makes detail views deep-linkable and makes the browser Back button pop
  // detail → list the way users expect.
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  // `/new` is an UNSAVED draft: the editor renders but nothing is persisted (and so nothing shows
  // in the list) until the first Save. A real id means an existing, persisted contract.
  const isNew = id === 'new';
  const activeId = id && id !== 'new' ? Number(id) : null;
  const NEW_PATH = routes.pricingContractDetailPath.replace(':id', 'new');
  const [loaded, setLoaded] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const refresh = useCallback(
    () => Promise.all([refreshContracts(), refreshCurves()]),
    [refreshContracts, refreshCurves]
  );

  useEffect(() => {
    refresh()
      .catch((e: Error) => setToast({ type: 'error', message: e.message }))
      .finally(() => setLoaded(true));
  }, [refresh]);

  // Show user contracts only (canonical/synthetic definitions are managed in Curve Management).
  const contracts = useMemo(
    () => allContracts.filter((c) => !c.curveId),
    [allContracts]
  );
  const curveOptions: CurveOption[] = useMemo(
    () => curves.map((c) => ({ value: c.id, label: c.name })),
    [curves]
  );

  const activeContract = contracts.find((c) => c.id === activeId) ?? null;
  const pendingDelete = contracts.find((c) => c.id === deleteId) ?? null;

  // A blank, UNSAVED draft rendered for the `/new` route. Not persisted until the first Save, so
  // it never appears in the list. A default Constant leg satisfies published-needs-legs; the
  // default name is a placeholder the editor blocks Save on (the user must rename it first).
  const draft = useMemo<Contract>(
    () => ({
      id: 0,
      href: '',
      auth: [],
      createdAt: '',
      updatedAt: '',
      name: `New contract ${contracts.length + 1}`,
      curveId: null,
      compositionType: null,
      origin: 'pricing_contract',
      status: 'published',
      commodity: 'crude',
      createdBy: null,
      updatedBy: null,
      legs: [{ legId: 1, curveId: null, multiplier: 1 }],
      combinedFormula: '',
    }),
    [contracts.length]
  );
  const editorContract = isNew ? draft : activeContract;

  const openContract = useCallback(
    (cid: number) =>
      history.push(
        routes.pricingContractDetailPath.replace(':id', String(cid))
      ),
    [history]
  );

  const backToList = useCallback(
    () => history.push(routes.pricingContractBuilderPath),
    [history]
  );

  // Deep-link / stale-link guard: an id in the URL that resolves to no contract
  // (never existed, or was just deleted) bounces back to the list once data has loaded.
  useEffect(() => {
    if (loaded && activeId !== null && !activeContract) backToList();
  }, [loaded, activeId, activeContract, backToList]);

  // "Add" opens a blank draft at `/new` WITHOUT persisting — the record is created on first Save,
  // so it stays out of the list until then.
  const addContract = () => history.push(NEW_PATH);

  const duplicateContract = async () => {
    if (!activeContract) return;
    try {
      const saved = await pricingApi.createContract({
        name: `${activeContract.name} (copy)`,
        status: 'published',
        commodity: activeContract.commodity,
        legs: activeContract.legs.map((l) => ({ ...l })),
      });
      await refresh();
      openContract(saved.id);
      setToast({
        type: 'success',
        message: 'Contract cloned',
        subtitle: saved.name,
      });
    } catch (e) {
      setToast({ type: 'error', message: (e as Error).message });
    }
  };

  // Save creates the record on a `/new` draft (then navigates to its real id, adding it to the
  // list), or updates the existing one.
  const saveContract = async (
    payload: Partial<Contract>
  ): Promise<Contract> => {
    try {
      const saved = isNew
        ? await pricingApi.createContract(payload)
        : await pricingApi.updateContract(activeId as number, payload);
      await refresh();
      if (isNew) openContract(saved.id);
      setToast({
        type: 'success',
        message: isNew ? 'Contract created' : 'Changes saved',
        subtitle: saved.name,
      });
      return saved;
    } catch (e) {
      setToast({ type: 'error', message: (e as Error).message });
      throw e;
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const name = contracts.find((c) => c.id === deleteId)?.name ?? '';
    try {
      await pricingApi.deleteContract(deleteId);
      await refresh();
      if (activeId === deleteId) backToList();
      setToast({
        type: 'success',
        message: 'Contract deleted',
        subtitle: name,
      });
    } catch (e) {
      setToast({ type: 'error', message: (e as Error).message });
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <>
      {activeId === null && !isNew && (
        <ContractLibrary
          contracts={contracts}
          onOpen={openContract}
          onAdd={addContract}
        />
      )}

      {editorContract && (
        <ContractEditor
          key={isNew ? 'new' : editorContract.id}
          contract={editorContract}
          isNew={isNew}
          curveOptions={curveOptions}
          onSave={saveContract}
          onDuplicate={duplicateContract}
          onRequestDelete={() =>
            activeContract && setDeleteId(activeContract.id)
          }
        />
      )}

      <Modal
        open={Boolean(pendingDelete)}
        danger
        size="sm"
        modalHeading={
          pendingDelete ? `Delete “${pendingDelete.name}” contract?` : ''
        }
        modalLabel="Pricing Contracts"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={() => setDeleteId(null)}
        onRequestSubmit={confirmDelete}
      >
        This cannot be undone. The contract and all of its legs will be
        permanently removed.
      </Modal>

      {toast?.type === 'success' && (
        <SuccessToast
          message={toast.message}
          subtitle={toast.subtitle}
          onClose={() => {
            setToast(null);
            return true;
          }}
        />
      )}
      {toast?.type === 'error' && (
        <ErrorToast
          message={toast.message}
          subtitle={toast.subtitle}
          onClose={() => {
            setToast(null);
            return true;
          }}
        />
      )}
    </>
  );
}
